import React, { useEffect, useRef } from 'react';
import './EnergyVisualizer.css';

// Wave class for gentle energy animation
class Wave {
  constructor(centerX, centerY, maxRadius, rgb) {
    this.x = centerX;
    this.y = centerY;
    this.radius = 0;
    this.maxRadius = maxRadius;
    this.speed = 0.15; // Viel langsamer (vorher 0.3)
    this.opacity = 1;
    this.rgb = rgb;
  }

  update() {
    this.radius += this.speed;
    this.opacity = 1 - this.radius / this.maxRadius;
  }

  draw(ctx) {
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(${this.rgb.r}, ${this.rgb.g}, ${this.rgb.b}, ${this.opacity * 0.12})`; // Noch dezenter
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }

  isDead() {
    return this.radius >= this.maxRadius;
  }
}

// Particle class for floating energy particles
class Particle {
  constructor(centerX, centerY, rgb) {
    const angle = Math.random() * Math.PI * 2;
    const distance = Math.random() * 150;
    
    this.x = centerX + Math.cos(angle) * distance;
    this.y = centerY + Math.sin(angle) * distance;
    this.size = Math.random() * 3 + 1;
    this.speedX = (Math.random() - 0.5) * 0.25; // Langsamer (vorher 0.5)
    this.speedY = (Math.random() - 0.5) * 0.25; // Langsamer (vorher 0.5)
    this.opacity = Math.random() * 0.4 + 0.2; // Etwas dezenter
    this.fadeRate = Math.random() * 0.001 + 0.0005; // Langsamer Fade
    this.rgb = rgb;
    this.life = 1;
  }

  update() {
    this.x += this.speedX;
    this.y += this.speedY;
    this.life -= this.fadeRate;
    this.opacity = this.life * 0.6;
  }

  draw(ctx) {
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(${this.rgb.r}, ${this.rgb.g}, ${this.rgb.b}, ${this.opacity})`;
    ctx.fill();
    
    // Sanftes Glühen um Partikel
    const gradient = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.size * 3);
    gradient.addColorStop(0, `rgba(${this.rgb.r}, ${this.rgb.g}, ${this.rgb.b}, ${this.opacity * 0.3})`);
    gradient.addColorStop(1, `rgba(${this.rgb.r}, ${this.rgb.g}, ${this.rgb.b}, 0)`);
    ctx.fillStyle = gradient;
    ctx.fill();
  }

  isDead() {
    return this.life <= 0;
  }
}

function EnergyVisualizer({ color, isActive }) {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    if (!isActive || !color) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let waves = [];
    let particles = [];
    let animationId;
    
    // Store current canvas dimensions for wave/particle creation
    let canvasDimensions = { width: 0, height: 0, centerX: 0, centerY: 0 };

    // Set canvas size - use document.documentElement for accurate viewport size
    const resizeCanvas = () => {
      // Force canvas to use full viewport dimensions
      const width = Math.max(
        document.documentElement.clientWidth,
        window.innerWidth || 0
      );
      const height = Math.max(
        document.documentElement.clientHeight,
        window.innerHeight || 0
      );
      
      canvas.width = width;
      canvas.height = height;
      
      // Update stored dimensions
      canvasDimensions = {
        width: width,
        height: height,
        centerX: width / 2,
        centerY: height / 2
      };
      
      // Clear waves and particles on resize to recenter
      waves = [];
      particles = [];
      
      console.log('Canvas resized - Energy Glow Center:', canvasDimensions);
    };
    
    // Initial resize with small delay to ensure DOM is ready
    setTimeout(resizeCanvas, 100);
    window.addEventListener('resize', resizeCanvas);

    // Parse hex color to RGB
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
          }
        : { r: 194, g: 164, b: 90 };
    };

    const rgb = hexToRgb(color);
    
    // Glow animation properties
    let glowScale = 1;
    let glowDirection = 1;

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw central glow (radial gradient) - DIREKT IM CANVAS
      const glowRadius = 150 * glowScale;
      const gradient = ctx.createRadialGradient(
        canvasDimensions.centerX, 
        canvasDimensions.centerY, 
        0,
        canvasDimensions.centerX, 
        canvasDimensions.centerY, 
        glowRadius
      );
      gradient.addColorStop(0, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${0.4 * glowScale})`);
      gradient.addColorStop(0.7, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${0.1 * glowScale})`);
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Animate glow pulsing (viel langsamer)
      glowScale += 0.0015 * glowDirection; // Vorher 0.005
      if (glowScale >= 1.08 || glowScale <= 1) { // Vorher 1.2
        glowDirection *= -1;
      }

      // Create new wave periodically (noch weniger häufig)
      if (Math.random() < 0.004) { // Vorher 0.008
        const maxRadius = Math.max(canvasDimensions.width, canvasDimensions.height);
        waves.push(new Wave(canvasDimensions.centerX, canvasDimensions.centerY, maxRadius, rgb));
      }

      // Create new particles periodically (weniger)
      if (Math.random() < 0.08) { // Vorher 0.15
        particles.push(new Particle(canvasDimensions.centerX, canvasDimensions.centerY, rgb));
      }

      // Update and draw waves
      waves = waves.filter((wave) => {
        wave.update();
        wave.draw(ctx);
        return !wave.isDead();
      });

      // Update and draw particles
      particles = particles.filter((particle) => {
        particle.update();
        particle.draw(ctx);
        return !particle.isDead();
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();
    animationRef.current = animationId;

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [color, isActive]);

  if (!isActive || !color) {
    return null;
  }

  return (
    <div className="energy-visualizer">
      <canvas ref={canvasRef} className="energy-canvas" />
    </div>
  );
}

export default EnergyVisualizer;
